import {Injectable} from '@angular/core';
import {AuthUser} from '@cddng/auth-oidc-apim';

import {environment} from 'src/environments/environment';
import {AuthUserDto} from 'src/app/shared/dto/authUserDto';
import {
  ExpeditionAutoProdRoleList,
  ExpeditionAutoShowRoleList,
  ExpeditionManuelProdRoleList,
  RechercheConsultationRoleList,
  Role,
  SITES_EXP_MAN,
  SITES_EXP_MAN_ET_AUTO,
  SpidOpexRoles,
  SupervisionProdRoleList,
  SupervisionShowRoleList
} from 'src/app/shared/enums/role.enum';

@Injectable({ providedIn: 'root' })
export class AppContext {

  private _authenticatedUser = new AuthUserDto();

  get authenticatedUser(): AuthUserDto {
    return this._authenticatedUser;
  }

  set authenticatedUser(authenticatedUser: AuthUser | AuthUserDto) {
    if (authenticatedUser instanceof AuthUserDto) {
      this._authenticatedUser = authenticatedUser;
    } else {
      this._authenticatedUser = this.determineUserRole(authenticatedUser);
    }
  }

  setBandeauScript(): void {
    const node = document.createElement('script');
    node.id = 'bandeau-script';
    node.src = environment.bandeau_script;
    node.type = 'text/javascript';
    node.async = true;
    document.getElementsByTagName('head')[0].appendChild(node);
  }

  /**
   * Determine user role for current connected user by regateCode (site) and role
   * @param authenticatedUser AuthUser
   * @return AuthUserDto
   */
  determineUserRole(authenticatedUser: AuthUser): AuthUserDto {
    const authUser: AuthUserDto = new AuthUserDto(authenticatedUser);
    if (authenticatedUser?.roles?.includes(Role.ADMIN)) {
      authUser.isAdmin = true;
      return authUser;
    }

    return this.setRoleByLocatisationSite(authUser);

  }

  /**
   * Set Role to the current user by regateCode (site) and role
   * @param authUser AuthUserDto
   * @return AuthUserDto
   */
  setRoleByLocatisationSite(authUser: AuthUserDto): AuthUserDto {
    if (SITES_EXP_MAN_ET_AUTO.includes(authUser.regateCode)) {
      authUser.isExpeditionManuelProd = ExpeditionManuelProdRoleList.some(el => authUser.roles.includes(el));
      authUser.isExpeditionAutoProd = ExpeditionAutoProdRoleList.some(el => authUser.roles.includes(el));
      authUser.isExpeditionAutoShow = ExpeditionAutoShowRoleList.some(el => authUser.roles.includes(el));
      authUser.isSupervisionProd = SupervisionProdRoleList.some(el => authUser.roles.includes(el));
      authUser.isSupervisionShow = SupervisionShowRoleList.some(el => authUser.roles.includes(el));
      authUser.isRechercheConsultation = RechercheConsultationRoleList.some(el => authUser.roles.includes(el));
    } else if (SITES_EXP_MAN.includes(authUser.regateCode) || SpidOpexRoles.some(el => authUser.roles.includes(el))) {
      authUser.isExpeditionManuelProd = ExpeditionManuelProdRoleList.some(el => authUser.roles.includes(el));
      authUser.isRechercheConsultation = RechercheConsultationRoleList.some(el => authUser.roles.includes(el));
    }
    return authUser;
  }

  /**
   * calculate if current has at least one role
   */
  currentUserHasRole(): boolean {
    return this._authenticatedUser?.isAdmin || this._authenticatedUser?.isExpeditionManuelProd
      || this._authenticatedUser?.isExpeditionAutoProd || this._authenticatedUser?.isExpeditionAutoShow
      || this._authenticatedUser?.isSupervisionProd || this._authenticatedUser?.isSupervisionShow;
  }

  /**
   * calculate if current has expedition manuel role
   */
  currentUserHasExpeditionManuelRole(): boolean {
    return this._authenticatedUser?.isAdmin || this._authenticatedUser?.isExpeditionManuelProd;
  }

  /**
   * calculate if current has expedition auto role
   */
  currentUserHasExpeditionAutoRole(): boolean {
    return this._authenticatedUser?.isAdmin || this._authenticatedUser?.isExpeditionAutoProd || this._authenticatedUser?.isExpeditionAutoShow;
  }

  /**
   * calculate if current has expedition auto prod role
   */
  currentUserHasExpeditionAutoProdRole(): boolean {
    return this._authenticatedUser?.isAdmin || this._authenticatedUser?.isExpeditionAutoProd;
  }

  /**
   * calculate if current has supervision role
   */
  currentUserHasSupervisionRole(): boolean {
    return this._authenticatedUser?.isAdmin || this._authenticatedUser?.isSupervisionProd || this._authenticatedUser?.isSupervisionShow;
  }

  /**
   * calculate if we show 'Plan de production' and 'Extraction conteneurs moins de 72h' or not
   */
  isShowingPlanProductionAndExtract72h(): boolean {
    return (this.currentUserHasSupervisionRole() && SITES_EXP_MAN_ET_AUTO.includes(this._authenticatedUser?.regateCode)) || this._authenticatedUser?.isAdmin;
  }

  /**
   * calculate if current has supervision prod role
   */
  currentUserHasSupervisionProdRole(): boolean {
    return this._authenticatedUser?.isAdmin || this._authenticatedUser?.isSupervisionProd;
  }

  /**
   * calculate if current user has consultation role
   */
  currentUserHasRechercheConsultationRole(): boolean {
    return this._authenticatedUser?.isAdmin || this._authenticatedUser?.isRechercheConsultation;
  }

  /**
   * calculate if current user has role ADMIN
   */
  currentUserHasRoleAdmin(): boolean {
    return this._authenticatedUser?.isAdmin;
  }

}
